import {
  ApiComponentType,
  ApiProjectSettingKey,
  ApiSimpleComponent,
} from "@incendium/api";
import { deleteComponent, saveComponent } from "features/campaigns";
import { usePollPendingComponents } from "features/campaigns/hooks/usePollPendingComponents";
import { useLanderBlocks } from "features/lightningLanders/hooks/useLanderBlocks";
import NewBlockDialog from "features/lightningLanders/components/NewBlockDialog";
import {
  landerDefaultTheme,
} from "features/lightningLanders/providers/LightningLanderThemeProvider";
import { BlocksList } from "features/sharedComponentBlocks/components/BlocksList";
import { useSelectedProject } from "Hooks";
import { friendlyDateTime } from "Helpers/dates";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import produce from "immer";

function LightningBlocksPage() {
  const { selectedProject, projectSettings } = useSelectedProject();
  const { blocks, setBlocks, fetched } = useLanderBlocks();
  const history = useHistory();
  const [dialogOpen, setDialogOpen] = useState(false);

  usePollPendingComponents({
    projectId: selectedProject?.id,
    components: blocks,
    setComponents: setBlocks,
    interval: 4000,
    enabled: !!selectedProject?.id && blocks.length > 0,
  });

  const newBlock = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const editLandingBlock = useCallback(
    (block: ApiSimpleComponent) => {
      history.push(`./lightning-landing-blocks/${block.id}`);
    },
    [history]
  );

  const onDelete = useCallback(
    async (block: ApiSimpleComponent): Promise<string> => {
      await deleteComponent(selectedProject?.id, block.id);
      return `Block ${block.name} deleted`;
    },
    [selectedProject]
  );

  const handleConfirmNewBlock = useCallback(
    async (name: string, aiComponent?: ApiSimpleComponent) => {
      if (!selectedProject?.id) return;

      let component: ApiSimpleComponent;
      if (aiComponent?.id) {
        component = aiComponent;
      } else {
        const theme =
          projectSettings?.[ApiProjectSettingKey.LANDING_THEME] ||
          JSON.stringify(landerDefaultTheme);
        const res = await saveComponent(selectedProject.id, {
          name: name || `Block (${friendlyDateTime(new Date())})`,
          type: ApiComponentType.LANDING_BLOCK,
          theme,
        });
        component = res.component!;
      }

      setBlocks((prev) =>
        produce(prev, (draft) => {
          draft.push(component);
        })
      );
      history.push(`./lightning-landing-blocks/${component.id}`);
    },
    [selectedProject, projectSettings, setBlocks, history]
  );

  return (
    <>
      <NewBlockDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmNewBlock}
      />
      <BlocksList
      blocks={blocks}
      setBlocks={setBlocks}
      fetched={fetched}
      title="Lightning Blocks"
      newButtonLabel="New Lightning Block"
      emptyMessage="You don't have any Blocks yet."
      emptySubMessage="Click the button below to get started!"
      onNew={newBlock}
      onEdit={editLandingBlock}
      onDelete={onDelete}
    />
    </>
  );
}

export default LightningBlocksPage;
